import { CellClassParams, IAggFuncParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import dayjs from 'dayjs';
import { PositionModel } from '../../../../../../Generated/Commodity-Management-Api';
import { ContractDateHelper } from '../../../../../../Helpers/ContractDateHelper';
import Formatting from '../../../../../../Helpers/Formatting';

export type DailyPositionsColumn =
  | 'TradeDate'
  | 'BudgetMonth'
  | 'Commodity'
  | 'Side'
  | 'PricingStartDate'
  | 'PricingEndDate'
  | 'VolumePerDay'
  | 'TotalVolume'
  | 'Unit'
  | 'Price'
  | 'TotalValue'
  | 'Counterparty';

export const DAILY_POSITIONS_ALL_COLUMNS: DailyPositionsColumn[] = [
  'TradeDate',
  'BudgetMonth',
  'Commodity',
  'Counterparty',
  'Side',
  'PricingStartDate',
  'PricingEndDate',
  'VolumePerDay',
  'Unit',
  'Price',
  'TotalVolume',
  'TotalValue',
];

export interface DailyPositionsReportConfiguration {
  behavior?: {
    groupByCommodity?: boolean;
  };
  columns?: {
    available?: DailyPositionsColumn[];
    active?: DailyPositionsColumn[];
  };
}

export interface DailyPositionsReportUserOverride {
  behavior?: {
    groupByCommodity?: boolean;
  };
  columns?: {
    active?: DailyPositionsColumn[];
  };
}

export interface DailyPositionsReportRow extends PositionModel {
  rowKey: string;
  path: string[];
  totalVolume: number;
  totalValue: number;
}

export function calculateTotalVolume(position: PositionModel): number {
  const start = position.pricingStartDate ? dayjs(position.pricingStartDate) : null;
  const end = position.pricingEndDate ? dayjs(position.pricingEndDate) : null;
  const volumePerDay = position.volume ?? 0;

  if (!start || !end || volumePerDay === 0) {
    return volumePerDay;
  }

  const days = end.diff(start, 'day') + 1;
  return days > 0 ? days * volumePerDay : volumePerDay;
}

function formatDate(value: string | null | undefined): string {
  return value ? dayjs(value).format('YYYY-MM-DD') : '';
}

interface WeightedPriceAggregate {
  totalVolume: number;
  totalValue: number;
}

function weightedPriceValueGetter(params: ValueGetterParams<DailyPositionsReportRow>): WeightedPriceAggregate | null {
  const row = params.data;
  if (!row) {
    return null;
  }
  return { totalVolume: row.totalVolume, totalValue: row.totalValue };
}

function weightedPriceAggFunc(params: IAggFuncParams<DailyPositionsReportRow, WeightedPriceAggregate | null>): WeightedPriceAggregate {
  let totalVolume = 0;
  let totalValue = 0;
  params.values.forEach((value) => {
    if (value) {
      totalVolume += value.totalVolume;
      totalValue += value.totalValue;
    }
  });
  return { totalVolume, totalValue };
}

function weightedPriceFormatter(params: ValueFormatterParams<DailyPositionsReportRow>): string {
  const value = params.value as WeightedPriceAggregate | null | undefined;
  if (!value || !value.totalVolume) {
    return '';
  }
  return Formatting.Price(value.totalValue / value.totalVolume);
}

function volumeFormatter(params: ValueFormatterParams<DailyPositionsReportRow>): string {
  return params.value == null ? '' : Formatting.Volume(Number(params.value));
}

function valueFormatter(params: ValueFormatterParams<DailyPositionsReportRow>): string {
  return params.value == null ? '' : Formatting.Value(Number(params.value));
}

interface DailyPositionsColumnConfiguration {
  colId: string;
  headerName: string;
  columnName?: string;
  field?: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  flex?: number;
  sortable?: boolean;
  hide?: boolean;
  filter?: string;
  cellClass?: string | string[] | ((params: CellClassParams<DailyPositionsReportRow, any>) => string | string[] | undefined);
  aggFunc?: string | ((params: IAggFuncParams<DailyPositionsReportRow, any>) => any);
  valueGetter?: (params: ValueGetterParams<DailyPositionsReportRow>) => any;
  valueFormatter?: (params: ValueFormatterParams<DailyPositionsReportRow>) => string;
}

export const DailyPositionsColumns: { [key in DailyPositionsColumn]: DailyPositionsColumnConfiguration } = {
  TradeDate: {
    colId: 'Trade Date',
    headerName: 'Trade Date',
    field: 'tradeDate',
    minWidth: 100,
    flex: 1,
    valueFormatter: (params) => formatDate(params.value),
  },
  BudgetMonth: {
    colId: 'Budget Month',
    headerName: 'Budget Month',
    minWidth: 130,
    flex: 1,
    valueGetter: (params) => {
      const budgetDate = params.data?.budgetDate;
      return budgetDate ? ContractDateHelper.toMonthYearString(budgetDate) : '';
    },
  },
  Commodity: {
    colId: 'Commodity',
    headerName: 'Commodity',
    field: 'commodity.commodityName',
    minWidth: 140,
    flex: 1,
  },
  Side: {
    colId: 'Side',
    headerName: 'Side',
    field: 'side',
    minWidth: 40,
    flex: 1,
  },
  PricingStartDate: {
    colId: 'Pricing Start',
    headerName: 'Pricing Start',
    field: 'pricingStartDate',
    minWidth: 125,
    flex: 1,
    valueFormatter: (params) => formatDate(params.value),
  },
  PricingEndDate: {
    colId: 'Pricing End',
    headerName: 'Pricing End',
    field: 'pricingEndDate',
    minWidth: 125,
    flex: 1,
    valueFormatter: (params) => formatDate(params.value),
  },
  VolumePerDay: {
    colId: 'Volume / Day',
    headerName: 'Volume / Day',
    field: 'volume',
    minWidth: 50,
    flex: 1,
    cellClass: 'justify-right',
    valueFormatter: volumeFormatter,
  },
  TotalVolume: {
    colId: 'Total Volume',
    headerName: 'Total Volume',
    field: 'totalVolume',
    minWidth: 50,
    flex: 1,
    cellClass: 'justify-right',
    aggFunc: 'sum',
    valueFormatter: volumeFormatter,
  },
  Unit: {
    colId: 'Unit',
    headerName: 'Unit',
    field: 'unit',
    minWidth: 5,
    flex: 1,
  },
  Price: {
    colId: 'Price',
    headerName: 'Price',
    minWidth: 50,
    flex: 1,
    cellClass: 'justify-right',
    valueGetter: weightedPriceValueGetter,
    aggFunc: weightedPriceAggFunc,
    valueFormatter: weightedPriceFormatter,
  },
  TotalValue: {
    colId: 'Total Value',
    headerName: 'Total Value',
    field: 'totalValue',
    minWidth: 50,
    flex: 1,
    cellClass: 'justify-right',
    aggFunc: 'sum',
    valueFormatter: valueFormatter,
  },
  Counterparty: {
    colId: 'Counterparty',
    headerName: 'Counterparty',
    field: 'counterparty.counterpartyName',
    minWidth: 125,
    flex: 1,
  },
};
